import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import api from "../api/axios";

const BrowseHistory = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      const viewed = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
      const limitedIds = viewed.slice(0, 10); // Max 10 products

      if (!limitedIds.length) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.post("/products/recently-viewed", { ids: limitedIds });
        setProducts(res.data.products || []);
      } catch (err) {
        console.error("Error fetching browse history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const clearHistory = () => {
    localStorage.removeItem("recentlyViewed");
    setProducts([]);
  };

  const removeProduct = (productId) => {
    const viewed = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
    const updated = viewed.filter((id) => id !== productId);
    localStorage.setItem("recentlyViewed", JSON.stringify(updated));
    setProducts(products.filter((p) => p._id !== productId));
  };

  const getImageUrl = (product) => {
    if (product?.imageUrl) return product.imageUrl;
    if (product?.images?.length > 0) return product.images[0].thumbnailUrl;
    if (product?.videos?.length > 0) return product.videos[0]?.thumbnailUrl;
    return "/placeholder-product.png";
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, minHeight: "100vh", background: "#f5f5f5" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, background: "#f5f5f5", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Browsing History
        </Typography>
        {products.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={clearHistory}
            sx={{ textTransform: "none" }}
          >
            Clear History
          </Button>
        )}
      </Box>

      {products.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No browsing history
          </Typography>
          <Typography color="textSecondary" mb={2}>
            Products you view will appear here
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            sx={{ background: "#ff9900", textTransform: "none" }}
          >
            Start Shopping
          </Button>
        </Card>
      ) : (
        <Box
          sx={{
            columnCount: { xs: 2, sm: 3, md: 4, lg: 5 },
            columnGap: 2,
          }}
        >
          {products.map((product) => (
            <Box
              key={product._id}
              sx={{
                breakInside: "avoid",
                mb: 2,
              }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  position: "relative",
                  transition: "0.2s",
                  "&:hover": { boxShadow: 6 },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 1,
                    bgcolor: "rgba(255,255,255,0.8)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                  }}
                  onClick={() => removeProduct(product._id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/product/${product._id}`)}
                >
                  <CardMedia
                    component="img"
                    image={getImageUrl(product)}
                    alt={product.title}
                    sx={{
                      width: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.3,
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {product.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#B12704",
                        fontSize: 14,
                      }}
                    >
                      ₹{Number(product.price || 0).toLocaleString("en-IN")}
                    </Typography>

                    {product.originalPrice && product.originalPrice > product.price && (
                      <Typography
                        sx={{
                          color: "#565959",
                          fontSize: 11,
                          textDecoration: "line-through",
                        }}
                      >
                        ₹{Number(product.originalPrice).toLocaleString("en-IN")}
                      </Typography>
                    )}
                  </CardContent>
                </Box>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BrowseHistory;